import React, { useState, useRef, useEffect } from "react";
import { sendPortfolioChat } from "../lib/api";
import { Send, Loader2, Sparkles, Brain } from "lucide-react";

/**
 * PortfolioChat — AI advisor chat with full portfolio context.
 * Pulls top holdings, sector weights, avg cost, manual assets, rule violations
 * into the system prompt so answers reference the user's actual book.
 */
export default function PortfolioChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `portfolio-chat-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (overrideMsg) => {
    const msg = (overrideMsg ?? input).trim();
    if (!msg || loading) return;
    if (!overrideMsg) setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await sendPortfolioChat(msg, sessionId);
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: typeof detail === "string" ? detail : "AI request failed. Check the backend logs for the provider error.",
      }]);
    }
    setLoading(false);
  };

  const suggestions = [
    "Am I over-concentrated in any sector?",
    "Which positions should I trim and why?",
    "What's my biggest portfolio risk right now?",
    "How's my asset allocation between equity, MF, and debt?",
  ];

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="portfolio-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-cyan-500/5 to-transparent">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
            <Brain size={14} className="text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">Ask Your AI Advisor</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono">GPT-5.2</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Portfolio-Aware</span>
            </div>
            <div className="text-[10px] text-[var(--text-muted)]">Ask anything about your holdings, sectors, risks, allocation</div>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={() => { setMessages([]); setInput(""); }}
            className="text-[10px] text-[var(--text-muted)] hover:text-white transition-colors"
            data-testid="portfolio-chat-clear"
          >
            Clear
          </button>
        )}
      </div>

      <div ref={scrollRef} className="max-h-[360px] min-h-[200px] overflow-y-auto px-4 py-3 space-y-3" data-testid="portfolio-chat-messages">
        {messages.length === 0 ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <Sparkles size={12} className="text-amber-400" />
              <span>Try asking:</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  disabled={loading}
                  className="text-left text-xs text-[var(--text-secondary)] bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2 hover:border-cyan-500/25 hover:text-white transition-colors disabled:opacity-40"
                  data-testid={`portfolio-chat-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${m.role === "user" ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-base)] border border-white/5 text-[var(--text-secondary)]"}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[var(--bg-base)] border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin text-cyan-400" />
              <span className="text-[10px] text-[var(--text-muted)]">Analyzing your portfolio...</span>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-white/10 p-3 bg-[var(--bg-base)]/30">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask about your portfolio..."
            className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-cyan-500/40"
            data-testid="portfolio-chat-input"
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-lg px-4 py-2 disabled:opacity-40 transition-colors flex items-center gap-1.5 text-xs font-medium"
            data-testid="portfolio-chat-send"
          >
            <Send size={14} />
            Ask
          </button>
        </div>
      </div>
    </div>
  );
}
